import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { CreditCard, Wallet, DollarSign, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

/** PayMongo Integration + Pass-on Fee settings. */
export function PayMongoTab({ paymongo, setPaymongo, savePaymongo, saving }) {
  return (
    <Card className="bg-[#041A3B] border-white/10">
      <CardHeader>
        <CardTitle className="text-white">PayMongo Integration</CardTitle>
        <CardDescription className="text-[#A0B4CC]">Configure payment gateway credentials</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between p-4 rounded-lg bg-[#000B18]">
          <div>
            <Label className="text-white">Enable PayMongo</Label>
            <p className="text-[#5C7599] text-xs mt-1">Switch between mock and live payment processing</p>
          </div>
          <Switch checked={paymongo.is_enabled} onCheckedChange={(val) => setPaymongo({ ...paymongo, is_enabled: val })} />
        </div>

        <div className="flex items-center justify-between p-4 rounded-lg bg-[#000B18]">
          <div>
            <Label className="text-white">Live Mode</Label>
            <p className="text-[#5C7599] text-xs mt-1">Use live keys instead of test keys</p>
          </div>
          <Switch checked={paymongo.is_live} onCheckedChange={(val) => setPaymongo({ ...paymongo, is_live: val })} />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-white">Public Key</Label>
            <Input value={paymongo.public_key || ''} onChange={(e) => setPaymongo({ ...paymongo, public_key: e.target.value })} className="bg-[#000B18] border-white/20 text-white" placeholder="pk_test_..." data-testid="paymongo-public-key" />
          </div>
          <div className="space-y-2">
            <Label className="text-white">Secret Key</Label>
            <Input value={paymongo.secret_key || ''} onChange={(e) => setPaymongo({ ...paymongo, secret_key: e.target.value })} className="bg-[#000B18] border-white/20 text-white" placeholder="sk_test_..." type="password" data-testid="paymongo-secret-key" />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-white">Webhook Secret</Label>
          <Input value={paymongo.webhook_secret || ''} onChange={(e) => setPaymongo({ ...paymongo, webhook_secret: e.target.value })} className="bg-[#000B18] border-white/20 text-white" placeholder="whsec_..." type="password" data-testid="paymongo-webhook" />
        </div>

        <div className="space-y-2">
          <Label className="text-white">Webhook URL</Label>
          <p className="text-[#5C7599] text-xs mb-1">Copy this URL and paste it into your PayMongo webhook settings</p>
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value="https://allglobalaccess.com/api/paymongo/webhook"
              className="bg-[#000B18] border-white/20 text-[#00E5FF] font-mono text-sm"
              data-testid="paymongo-webhook-url"
            />
            <Button
              type="button"
              variant="ghost"
              className="text-[#00E5FF] hover:bg-[#00E5FF]/10 shrink-0"
              onClick={() => {
                navigator.clipboard.writeText("https://allglobalaccess.com/api/paymongo/webhook");
                toast.success('Webhook URL copied!');
              }}
              data-testid="copy-webhook-url-btn"
            >
              Copy
            </Button>
          </div>
        </div>

        <FeeBlock
          title="Card Payments"
          subtitle="Visa, Mastercard, etc."
          icon={CreditCard}
          accent="text-[#007AFF]"
          accentBg="bg-[#007AFF]/20"
          mdrKey="card_mdr"
          fixedKey="card_fixed_fee"
          paymongo={paymongo}
          setPaymongo={setPaymongo}
        />
        <FeeBlock
          title="GCash"
          subtitle="GCash e-wallet payments"
          icon={Wallet}
          accent="text-[#00E5FF]"
          accentBg="bg-[#00E5FF]/20"
          mdrKey="gcash_mdr"
          fixedKey="gcash_fixed_fee"
          paymongo={paymongo}
          setPaymongo={setPaymongo}
        />
        <FeeBlock
          title="Maya"
          subtitle="Maya / PayMaya payments"
          icon={DollarSign}
          accent="text-[#00E676]"
          accentBg="bg-[#00E676]/20"
          mdrKey="maya_mdr"
          fixedKey="maya_fixed_fee"
          paymongo={paymongo}
          setPaymongo={setPaymongo}
        />

        <div className="p-3 rounded-lg bg-[#00E5FF]/5 border border-[#00E5FF]/15">
          <p className="text-[#A0B4CC] text-xs">
            <span className="text-[#00E5FF] font-medium">Example:</span> For a PHP 1,000 plan via Card — SP = (1000 + {paymongo.card_fixed_fee || 0}) / (1 - {((paymongo.card_mdr || 0) / 100).toFixed(4)}) = PHP {((1000 + (paymongo.card_fixed_fee || 0)) / (1 - (paymongo.card_mdr || 0) / 100)).toFixed(2)} | Fee = PHP {(((1000 + (paymongo.card_fixed_fee || 0)) / (1 - (paymongo.card_mdr || 0) / 100)) - 1000).toFixed(2)}
          </p>
        </div>

        {!paymongo.is_enabled && (
          <div className="p-3 rounded-lg bg-[#FFEA00]/5 border border-[#FFEA00]/20">
            <p className="text-[#FFEA00] text-xs">PayMongo is disabled. The platform is using mock payments. Enable and add your API keys to process real payments.</p>
          </div>
        )}

        {/* ── Disbursements (Money Movement) — separate PayMongo product ── */}
        <div className="border-t border-white/10 pt-5 mt-2">
          <h3 className="text-white font-medium text-base mb-1">Disbursements (Auto Payouts)</h3>
          <p className="text-[#5C7599] text-xs mb-4">
            Optional. Requires a separate contract with PayMongo Money Movement.
            When enabled, member withdrawals at <code>/admin/withdrawals</code> can be
            sent directly via PayMongo InstaPay / PESONet with a single click.
          </p>
          <div className="flex items-center justify-between p-4 rounded-lg bg-[#000B18] mb-4">
            <div>
              <Label className="text-white">Enable Auto-Disbursement</Label>
              <p className="text-[#5C7599] text-xs mt-1">When OFF, withdrawals stay manual.</p>
            </div>
            <Switch
              checked={!!paymongo.disbursement_enabled}
              onCheckedChange={(val) => setPaymongo({ ...paymongo, disbursement_enabled: val })}
              data-testid="paymongo-disburse-enabled"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white">Wallet ID</Label>
              <Input
                value={paymongo.disbursement_wallet_id || ''}
                onChange={(e) => setPaymongo({ ...paymongo, disbursement_wallet_id: e.target.value })}
                className="bg-[#000B18] border-white/20 text-white font-mono text-sm"
                placeholder="wallet_..." data-testid="paymongo-disburse-wallet"
              />
              <p className="text-[#5C7599] text-[11px]">PayMongo wallet that funds payouts.</p>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Disbursement Webhook Secret</Label>
              <Input
                type="password"
                value={paymongo.disbursement_webhook_secret || ''}
                onChange={(e) => setPaymongo({ ...paymongo, disbursement_webhook_secret: e.target.value })}
                className="bg-[#000B18] border-white/20 text-white"
                placeholder="whsec_..." data-testid="paymongo-disburse-webhook"
              />
              <p className="text-[#5C7599] text-[11px]">For verifying status callbacks.</p>
            </div>
          </div>
          <div className="space-y-2 mt-3">
            <Label className="text-white">Minimum Withdrawal (PHP)</Label>
            <Input
              type="number" min="0" step="50"
              value={paymongo.min_withdrawal_amount ?? 1000}
              onChange={(e) => setPaymongo({ ...paymongo, min_withdrawal_amount: parseFloat(e.target.value) || 0 })}
              className="bg-[#000B18] border-white/20 text-white max-w-xs"
              data-testid="paymongo-min-withdrawal"
            />
          </div>
          <div className="space-y-2 mt-3">
            <Label className="text-white">Disbursement Webhook URL</Label>
            <p className="text-[#5C7599] text-xs mb-1">Paste into PayMongo dashboard → Webhooks (subscribe to `wallet_transaction.*`).</p>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value="https://allglobalaccess.com/api/payouts/paymongo/webhook"
                className="bg-[#000B18] border-white/20 text-[#00E5FF] font-mono text-sm"
                data-testid="paymongo-disburse-webhook-url"
              />
              <Button
                type="button" variant="ghost"
                className="text-[#00E5FF] hover:bg-[#00E5FF]/10 shrink-0"
                onClick={() => {
                  navigator.clipboard.writeText("https://allglobalaccess.com/api/payouts/paymongo/webhook");
                  toast.success('Disbursement webhook URL copied!');
                }}
              >Copy</Button>
            </div>
          </div>
        </div>

        <Button onClick={savePaymongo} disabled={saving} className="btn-primary" data-testid="save-paymongo-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save PayMongo Settings
        </Button>
      </CardContent>
    </Card>
  );
}

function FeeBlock({ title, subtitle, icon: Icon, accent, accentBg, mdrKey, fixedKey, paymongo, setPaymongo }) {
  return (
    <div className="p-4 rounded-lg bg-[#000B18]">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-lg ${accentBg} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${accent}`} />
        </div>
        <div>
          <p className="text-white text-sm font-medium">{title}</p>
          <p className="text-[#5C7599] text-[10px]">{subtitle}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label className="text-[#A0B4CC] text-xs">MDR (%)</Label>
          <Input type="number" step="0.1" min="0" value={paymongo[mdrKey]} onChange={(e) => setPaymongo({ ...paymongo, [mdrKey]: parseFloat(e.target.value) || 0 })} className="bg-[#041A3B] border-white/20 text-white" data-testid={mdrKey.replace('_', '-')} />
        </div>
        <div className="space-y-1">
          <Label className="text-[#A0B4CC] text-xs">Fixed Fee (PHP)</Label>
          <Input type="number" step="0.5" min="0" value={paymongo[fixedKey]} onChange={(e) => setPaymongo({ ...paymongo, [fixedKey]: parseFloat(e.target.value) || 0 })} className="bg-[#041A3B] border-white/20 text-white" data-testid={fixedKey.replace(/_/g, '-')} />
        </div>
      </div>
    </div>
  );
}
